import { AuthAdapter, OtpSession, VerifyResult } from './authAdapter'
import { apiClient } from '../../lib/api'
import {
  sendOTP,
  verifyOTP,
  getFirebaseIdToken,
  formatPhoneNumberForFirebase
} from '../../lib/firebase'
import { RecaptchaVerifier } from '../../lib/firebase'

/**
 * Firebase Auth Adapter
 * Uses Firebase SDK for client-side phone verification
 * Sends Firebase ID token to backend for session management
 * 
 * Verifier is passed directly from the component
 */
export class FirebaseAuthAdapter implements AuthAdapter {
  private confirmationResults = new Map<string, any>()

  async sendOtp(phone: string, verifier?: RecaptchaVerifier): Promise<OtpSession> {
    try {
      // Normalize phone number
      const formattedPhone = formatPhoneNumberForFirebase(phone)

      // Verifier must be provided by the component
      if (!verifier) {
        throw new Error('Recaptcha verifier is required')
      }

      // Send OTP via Firebase SDK
      const result = await sendOTP(formattedPhone, verifier)
      
      // Generate a session ID to track this verification
      const sessionId = this.generateSessionId()
      
      // Store confirmation result for later verification
      this.confirmationResults.set(sessionId, result.confirmationResult)

      // Auto-cleanup after 10 minutes
      setTimeout(() => {
        this.confirmationResults.delete(sessionId)
      }, 10 * 60 * 1000)

      return {
        sessionId,
        phone: formattedPhone
      }
    } catch (error: any) {
      console.error('Firebase send OTP error:', error)
      
      // Handle common Firebase errors
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many requests. Please try again later.')
      } else if (error.code === 'auth/invalid-phone-number') {
        throw new Error('Invalid phone number format.')
      } else if (error.code === 'auth/invalid-app-credential' || error.message?.includes('already rendered')) {
        // Verifier needs to be reset - this will be handled by the context
        throw new Error('Verification session expired. Please try again.')
      }
      
      throw new Error(error.message || 'Failed to send OTP')
    }
  }

  async verifyOtp(sessionId: string, otp: string): Promise<VerifyResult> {
    try {
      // Get the confirmation result for this session
      const confirmationResult = this.confirmationResults.get(sessionId)
      
      if (!confirmationResult) {
        throw new Error('Session expired or invalid. Please request OTP again.')
      }

      // Verify OTP with Firebase SDK
      const firebaseUser = await verifyOTP(confirmationResult, otp)

      if (!firebaseUser) {
        throw new Error('Verification failed')
      }

      // Get Firebase ID token
      const firebaseToken = await getFirebaseIdToken(firebaseUser)

      // Send Firebase token to backend for verification and session creation
      const response = await apiClient.post('/auth/verify-otp', {
        sessionId: firebaseToken, // Backend expects Firebase token as sessionId
        otp: otp // Backend ignores this for Firebase tokens
      })

      // Clear the confirmation result as it's been used
      this.confirmationResults.delete(sessionId)

      if ((response.data as any).success) {
        const data = (response.data as any).data
        return {
          sessionId: data.sessionId, // New session ID from backend
          phone: data.phone,
          exists: data.exists,
          availableRoles: data.availableRoles || []
        }
      } else {
        throw new Error((response.data as any).error || 'Verification failed')
      }
    } catch (error: any) {
      console.error('Firebase verify OTP error:', error)
      
      // Handle common Firebase errors
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid OTP code. Please try again.')
      } else if (error.code === 'auth/code-expired') {
        throw new Error('OTP expired. Please request a new one.')
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Invalid OTP'
      throw new Error(errorMessage)
    }
  }

  /**
   * Generate a unique session ID for tracking confirmation results
   */
  private generateSessionId(): string {
    return `firebase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

